// src/components/booking/PractitionerCancelModal.js
// Enhanced practitioner cancel modal with reason, message to client, and refund info.
import React, { useState } from 'react';
import { X } from 'lucide-react';

const REASON_OPTIONS = [
  { value: '', label: 'Select a reason... (optional)' },
  { value: 'schedule_conflict', label: 'Schedule conflict' },
  { value: 'personal', label: 'Personal reasons' },
  { value: 'practitioner_unavailable', label: 'Practitioner unavailable' },
  { value: 'client_noshow', label: 'Client no-show' },
  { value: 'other', label: 'Other' },
];

const PractitionerCancelModal = ({ booking, clientName, onClose, onConfirm, cancelling }) => {
  const [reason, setReason] = useState('');
  const [message, setMessage] = useState('');

  const price = booking?.service?.price || 0;
  const refundDisplay = price > 0 ? `$${price.toFixed(2)}` : null;

  const handleConfirm = () => {
    onConfirm({
      reason: reason ? { category: reason, details: null } : null,
      practitionerMessage: message.trim() || null,
    });
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 px-4"
      onClick={() => { if (!cancelling) onClose(); }}
    >
      <div
        className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-gray-900">Cancel Session</h2>
          <button
            onClick={onClose}
            disabled={cancelling}
            className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Booking details card */}
        <div className="bg-gray-50 rounded-lg border border-gray-200 p-4 mb-4">
          <p className="text-sm font-semibold text-gray-900 mb-1">
            Client: {clientName}
          </p>
          <p className="text-sm text-gray-600 mb-1">
            {booking?.service?.name || 'Session'}
          </p>
          <p className="text-sm text-gray-500">
            {booking?._dateStr || ''}
          </p>
        </div>

        {/* Refund notice */}
        {refundDisplay && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 mb-4">
            <p className="text-sm text-amber-800">
              <span className="font-medium">⚠ The client will receive a full refund of {refundDisplay}</span>
            </p>
          </div>
        )}

        {/* Reason dropdown */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Why are you cancelling?
          </label>
          <select
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            disabled={cancelling}
            className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
          >
            {REASON_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>

        {/* Message to client */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Message to client (optional)
          </label>
          <div className="relative">
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value.slice(0, 300))}
              placeholder="Let your client know why you're cancelling (optional)"
              disabled={cancelling}
              rows={3}
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm text-gray-700 resize-none focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
            />
            <span className={`absolute bottom-2 right-3 text-xs ${message.length >= 280 ? 'text-red-500' : 'text-gray-400'}`}>
              {message.length}/300
            </span>
          </div>
        </div>

        {/* Buttons */}
        <div className="flex gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={cancelling}
            className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            Keep session
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={cancelling}
            className="flex-1 px-4 py-2.5 bg-rose-600 text-white rounded-lg text-sm font-semibold hover:bg-rose-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {cancelling ? 'Cancelling…' : 'Cancel session'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PractitionerCancelModal;
